import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { LEAccordion, LEAccordionItem } from '../../../components/LEAccordion';
import LEAccordionContainer from '../../../components/LEAccordion/LeAccordionContainer';
import { ACCORDION_NAME } from '../../../components/LEAccordion/utils';
import Loader from '../../../components/Loader';
import {
  LICENSE_PLATE_NAME,
  VIN_NAME,
} from '../../../components/licensePlateOrVinInput/LicensePlateOrVinInput';
import { isProd } from '../../../config';
import { COUNTRY_CONFIG, getTempInfoCountry } from '../../../constants/countries';
import {
  TemporaryData,
  useAffiliateDetailsQuery,
  useCheckAffiliateDealExistsQuery,
} from '../../../gql/generated/graphql';
import AffiliateDevToolbar, { devIgnoreExistingDealOverride } from './AffiliateDevToolbar';
import SinglePageSubmitHeader from './SinglePageSubmitHeader';
import AffiliateSinglePageAddressDetails from './accordions/AffiliateSinglePageAddressDetails';
import AffiliateSinglePageCarDetails from './accordions/AffiliateSinglePageCarDetails';
import AffiliateSinglePageEmploymentDetails from './accordions/AffiliateSinglePageEmploymentDetails';
import AffiliateSinglePageLienholder from './accordions/AffiliateSinglePageLienholder';

const AffiliateSinglePage = ({
  affiliateTempInfoData,
}: {
  affiliateTempInfoData?: TemporaryData;
}) => {
  const devIgnoreExistingDeal = useAtomValue(devIgnoreExistingDealOverride);
  const affiliateExternalId = affiliateTempInfoData?.affiliate_external_id;

  const { data: affiliateDealExistsData, loading: affiliateDealExistsLoading } =
    useCheckAffiliateDealExistsQuery({
      variables: {
        affiliateExternalId,
      },
      skip: !affiliateExternalId,
    });
  const { data: affiliateDetailsData, loading: affiliateDetailsLoading } = useAffiliateDetailsQuery(
    {
      variables: {
        affiliateExternalId,
      },
      skip: !affiliateExternalId,
    },
  );

  const country = getTempInfoCountry(affiliateTempInfoData);
  const [currentInputType, setCurrentInputType] = useState(
    COUNTRY_CONFIG[country].plateLookup ? LICENSE_PLATE_NAME : VIN_NAME,
  );
  const [licensePlateState, setLicensePlateState] = useState<string | null>(null);
  const [make, setMake] = useState<string | null>(null);
  const [lienholderNotAvailable, setLienholderNotAvailable] = useState(false);
  const [shownAccordions, setShownAccordions] = useState(1);

  const affiliateDetails = affiliateDetailsData?.affiliateDetails;

  const openAccordion = (targetIndex: number) => {
    setShownAccordions((currentIndex) => Math.max(currentIndex, targetIndex));
  };

  const renderContent = () => {
    if (
      affiliateDealExistsLoading ||
      affiliateDetailsLoading ||
      (isProd && !affiliateTempInfoData)
    ) {
      return <Loader />;
    }

    if (!affiliateTempInfoData) return null;

    const dealExists =
      !!affiliateDealExistsData?.checkAffiliateDealExists && !devIgnoreExistingDeal;

    if (lienholderNotAvailable || dealExists) {
      return (
        <SinglePageSubmitHeader
          affiliateTempInfoData={affiliateTempInfoData}
          affiliateDetails={affiliateDetails}
          lienholderNotAvailable={lienholderNotAvailable}
          dealExists={!lienholderNotAvailable && dealExists}
        />
      );
    }

    return (
      <>
        <SinglePageSubmitHeader
          affiliateTempInfoData={affiliateTempInfoData}
          affiliateDetails={affiliateDetails}
        />
        <LEAccordion name={ACCORDION_NAME.AFFILIATE_ACCORDION} minutes={1}>
          <LEAccordionItem
            accordionKey="affiliateVehicle"
            title={`WHAT IS YOUR VEHICLE'S ${
              LICENSE_PLATE_NAME === currentInputType ? 'LICENSE PLATE' : 'VIN'
            }?`}
          >
            <AffiliateSinglePageCarDetails
              currentInputType={currentInputType}
              setCurrentInputType={setCurrentInputType}
              setLicensePlateState={setLicensePlateState}
              setMake={setMake}
              showNextAccordion={() => openAccordion(2)}
              country={country}
            />
          </LEAccordionItem>
          {shownAccordions >= 2 && (
            <LEAccordionItem accordionKey="affiliateLienholder" title="WHO IS YOUR LEASE THROUGH?">
              <AffiliateSinglePageLienholder
                licensePlateState={licensePlateState}
                make={make}
                setLienholderNotAvailable={setLienholderNotAvailable}
                showNextAccordion={() => openAccordion(3)}
              />
            </LEAccordionItem>
          )}
          {shownAccordions >= 3 && (
            <LEAccordionItem accordionKey="affiliateAddress" title="FINAL RESIDENCE DETAILS">
              <AffiliateSinglePageAddressDetails
                affiliateTempInfoData={affiliateTempInfoData}
                showNextAccordion={() => openAccordion(4)}
              />
            </LEAccordionItem>
          )}
          {shownAccordions >= 4 && (
            <LEAccordionItem accordionKey="affiliateEmployment" title="FINAL EMPLOYMENT DETAILS">
              <AffiliateSinglePageEmploymentDetails />
            </LEAccordionItem>
          )}
        </LEAccordion>
      </>
    );
  };

  return (
    <LEAccordionContainer>
      {renderContent()}
      {!isProd && <AffiliateDevToolbar affiliateTempInfoData={affiliateTempInfoData} />}
    </LEAccordionContainer>
  );
};

export default AffiliateSinglePage;
